import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { SelectItem } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type {
  AdvertisingAd,
  AdvertisingRow,
  GetAdvertisingReportInput,
} from '@borradh-workspace/api-client/types';
import {
  ArrowDown,
  ArrowUp,
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Columns3,
  Download,
  Image,
  RefreshCw,
  Search,
  Video,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  fetchAdvertisingReport,
  useAdvertisingReport,
} from '../../api/advertising-report/advertising-report.hook';
import { AdvertisingDetail } from './advertising-detail';
import {
  AD_METRICS,
  METRIC_PRESETS,
  PRIORITY_METRICS,
  describeMetric,
  divide,
  formatCount,
  formatMetric,
  formatMoney,
} from './advertising-metrics';
import { buildCsv, sumValues } from './advertising-report';
import type { GroupBy, Sort } from './advertising-report';
import {
  DeliveryBadge,
  FilterSelect,
  Hint,
  Stat,
  currencyName,
  formatTimestamp,
} from './advertising-ui';

const EMPTY_ROWS: AdvertisingRow[] = [];
const PREFERENCES_KEY = 'borradh-admin-advertising-v1';
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (days: number) =>
  new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
const prettyDate = (date: string) =>
  new Intl.DateTimeFormat('en-IE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
const validDates = (since: string, until: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(since) &&
  /^\d{4}-\d{2}-\d{2}$/.test(until) &&
  since <= until &&
  Number.isFinite(Date.parse(since)) &&
  Number.isFinite(Date.parse(until)) &&
  (Date.parse(until) - Date.parse(since)) / 86_400_000 < 93;

function loadPreferences() {
  const fallback = {
    group: 'ads' as GroupBy,
    preset: 'overview',
    metrics: METRIC_PRESETS.overview.metrics,
  };
  try {
    const saved = JSON.parse(localStorage.getItem(PREFERENCES_KEY) ?? 'null');
    if (
      !saved ||
      !['ads', 'organizations', 'campaigns'].includes(saved.group) ||
      !Array.isArray(saved.metrics)
    )
      return fallback;
    const metrics = saved.metrics.filter(
      (id: unknown) =>
        typeof id === 'string' && AD_METRICS.some((metric) => metric.id === id)
    );
    return {
      group: saved.group as GroupBy,
      preset:
        typeof saved.preset === 'string' &&
        (METRIC_PRESETS[saved.preset] || saved.preset === 'custom')
          ? saved.preset
          : 'overview',
      metrics:
        saved.preset !== 'custom' && METRIC_PRESETS[saved.preset]
          ? METRIC_PRESETS[saved.preset].metrics
          : (metrics as string[]),
    };
  } catch {
    return fallback;
  }
}

function DateFilter({
  since,
  until,
  onChange,
}: {
  since: string;
  until: string;
  onChange: (since: string, until: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ since, until });
  const presets = [
    ['Last 7 days', daysAgo(6), today()],
    ['Last 30 days', daysAgo(29), today()],
    ['This month', `${today().slice(0, 7)}-01`, today()],
  ];
  return (
    <Popover
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) setDraft({ since, until });
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="font-normal">
          <CalendarDays className="size-4" />
          {prettyDate(since)} – {prettyDate(until)}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 max-w-[calc(100vw-2rem)] space-y-4"
      >
        <p className="text-sm font-medium">Reporting period</p>
        <div className="flex flex-wrap gap-2">
          {presets.map(([label, from, to]) => (
            <Button
              key={label}
              variant="outline"
              size="sm"
              onClick={() => {
                onChange(from, to);
                setOpen(false);
              }}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label
            htmlFor="advertising-since"
            className="space-y-1 text-xs text-muted-foreground"
          >
            From
            <Input
              id="advertising-since"
              aria-label="Reporting period start"
              type="date"
              value={draft.since}
              max={draft.until || today()}
              onChange={(event) =>
                setDraft({ ...draft, since: event.target.value })
              }
            />
          </label>
          <label
            htmlFor="advertising-until"
            className="space-y-1 text-xs text-muted-foreground"
          >
            To
            <Input
              id="advertising-until"
              aria-label="Reporting period end"
              type="date"
              value={draft.until}
              min={draft.since}
              max={today()}
              onChange={(event) =>
                setDraft({ ...draft, until: event.target.value })
              }
            />
          </label>
        </div>
        <p className="text-xs text-muted-foreground">
          Up to 93 days. Ad dates use the account timezone; conversation dates
          use UTC.
        </p>
        <Button
          className="w-full"
          size="sm"
          disabled={!validDates(draft.since, draft.until)}
          onClick={() => {
            onChange(draft.since, draft.until);
            setOpen(false);
          }}
        >
          Apply dates
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export function AdvertisingPage() {
  const [dates, setDates] = useState(() => ({
    since: daysAgo(29),
    until: today(),
  }));
  const [search, setSearch] = useState('');
  const [organization, setOrganization] = useState('all');
  const [campaign, setCampaign] = useState('all');
  const [status, setStatus] =
    useState<NonNullable<GetAdvertisingReportInput['status']>>('active');
  const [currency, setCurrency] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [format, setFormat] = useState('all');
  const [replies, setReplies] = useState('all');
  const [preferences, setPreferences] = useState(loadPreferences);
  const [sort, setSort] = useState<Sort>({ id: 'spend', direction: 'desc' });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState('25');
  const [selectedAd, setSelectedAd] = useState<AdvertisingAd | null>(null);
  const [exporting, setExporting] = useState(false);
  const exportController = useRef<AbortController | null>(null);
  useEffect(() => () => exportController.current?.abort(), []);
  const debouncedSearch = useDebouncedValue(search, 300);
  const input: GetAdvertisingReportInput = {
    ...dates,
    search: debouncedSearch,
    organization,
    campaign,
    status,
    currency,
    platform: platform as GetAdvertisingReportInput['platform'],
    format: format as GetAdvertisingReportInput['format'],
    replies: replies as GetAdvertisingReportInput['replies'],
    group: preferences.group,
    sort: sort.id as GetAdvertisingReportInput['sort'],
    direction: sort.direction,
    page,
    pageSize: Number(pageSize),
  };
  const query = useAdvertisingReport(input);
  const [exportError, setExportError] = useState<string | null>(null);
  const mobile = useIsMobile();
  useEffect(() => {
    try {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
    } catch {
      /* Storage is optional. */
    }
  }, [preferences]);
  const organizations = query.data?.facets.organizations ?? [];
  const currencies = query.data?.facets.currencies ?? [];
  const pageRows = query.data?.ads ?? EMPTY_ROWS;
  const totalRows = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / Number(pageSize)));
  const currentPage = query.data?.page ?? page;
  const summary = query.data?.summary;
  const totals = summary?.values ?? sumValues([]);
  const nativeCurrencies = summary?.currencies ?? [];
  const summaryCurrency =
    organization !== 'all' && nativeCurrencies.length === 1
      ? nativeCurrencies[0]
      : 'EUR';
  const summarySpend =
    summaryCurrency === 'EUR' ? (summary?.spendEur ?? null) : totals.spend;
  const cpm = divide(summarySpend, totals.impressions, 1000);
  const replyRate = divide(totals.replied, totals.conversations, 100);
  const costConversation = divide(summarySpend, totals.conversations);
  const conversionKind = summary?.conversionKind ?? 'mixed';
  const selectedMetricIds =
    METRIC_PRESETS[preferences.preset]?.metrics ?? preferences.metrics;
  const metrics = selectedMetricIds.flatMap((id) => {
    const metric = AD_METRICS.find((item) => item.id === id);
    return metric ? [describeMetric(metric, conversionKind)] : [];
  });
  const extraFilters =
    Number(platform !== 'all') +
    Number(format !== 'all') +
    Number(replies !== 'all');
  const hasFilters = Boolean(
    search ||
      organization !== 'all' ||
      campaign !== 'all' ||
      currency !== 'all' ||
      extraFilters ||
      status !== 'active'
  );
  const missingRates = summary?.missingRates ?? 0;
  const noData = summary?.noData ?? 0;
  const fxHint = query.data
    ? `Amounts converted to euros using ${query.data.fx.source}. Reference date: ${query.data.fx.rateDate ?? 'unavailable'}. Checked: ${formatTimestamp(query.data.fx.fetchedAt)}. ${query.data.fx.stale ? 'Refresh failed; using the last known rates.' : query.data.fx.fetchedAt ? 'Daily rates, refreshed every 24 hours.' : 'Rates unavailable.'} ${Object.entries(
        query.data.fx.ratesToEur
      )
        .map(
          ([code, rate]) =>
            `1 ${code} = ${rate.toFixed(6)} EUR${query.data.fx.rateDates[code] ? ` (${query.data.fx.rateDates[code]})` : ''}`
        )
        .join('; ')}.`
    : 'Exchange rates are loading.';
  const clear = () => {
    setSearch('');
    setOrganization('all');
    setCampaign('all');
    setCurrency('all');
    setPlatform('all');
    setFormat('all');
    setReplies('all');
    setStatus('active');
    setPage(0);
  };
  const changeSort = (id: string) => {
    setPage(0);
    setSort((previous) => ({
      id,
      direction:
        previous.id === id && previous.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const sortIcon = (id: string) =>
    sort.id !== id ? (
      <ChevronsUpDown aria-hidden className="size-3 opacity-40" />
    ) : sort.direction === 'asc' ? (
      <ArrowUp aria-hidden className="size-3" />
    ) : (
      <ArrowDown aria-hidden className="size-3" />
    );
  const chooseRow = (row: AdvertisingRow) => {
    if (!row.grouped) {
      setSelectedAd(row);
      return;
    }
    setOrganization(row.organizationId);
    if (preferences.group === 'campaigns' && row.campaignId)
      setCampaign(row.campaignId);
    setCurrency(row.currency ?? 'all');
    setPreferences({ ...preferences, group: 'ads' });
    setPage(0);
  };
  const exportRows = async () => {
    setExporting(true);
    const controller = new AbortController();
    exportController.current = controller;
    try {
      setExportError(null);
      const chunks: string[] = [];
      let exportTotal: number | undefined;
      for (let exportPage = 0; ; exportPage++) {
        const result = await fetchAdvertisingReport(
          { ...input, page: exportPage, pageSize: 100 },
          controller.signal
        );
        if (controller.signal.aborted) return;
        if (
          result.page !== exportPage ||
          (exportTotal !== undefined && result.total !== exportTotal)
        )
          throw new Error('Report changed during export');
        exportTotal = result.total;
        const csv = buildCsv(result.ads, metrics, dates.since, dates.until);
        chunks.push(exportPage === 0 ? csv : csv.slice(csv.indexOf('\r\n')));
        if ((exportPage + 1) * 100 >= result.total) break;
      }
      const blob = new Blob(chunks, { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `advertising-${preferences.group}-${dates.since}-${dates.until}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      if (!controller.signal.aborted)
        setExportError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className="mx-auto flex w-full max-w-[1600px] flex-col gap-5 px-4 py-6 md:px-6"
      data-testid="advertising-dashboard"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Advertising</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Ad performance across every organization.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void exportRows()}
            disabled={
              query.isFetching ||
              totalRows === 0 ||
              exporting ||
              search !== debouncedSearch
            }
          >
            <Download className="size-4" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw
              className={cn('size-4', query.isFetching && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="size-1.5 rounded-full bg-primary" />
          {query.data?.latestDataDate
            ? `Data through ${prettyDate(query.data.latestDataDate)}`
            : 'Loading stored Meta insights…'}
          {query.data?.fetchedAt && (
            <span>· Synced {formatTimestamp(query.data.fetchedAt)}</span>
          )}
        </span>
        <Hint label={fxHint}>
          <button
            type="button"
            className="cursor-help underline decoration-dotted underline-offset-4"
          >
            {query.data?.fx.rateDate
              ? `EUR conversion · FX ${query.data.fx.rateDate}${query.data.fx.stale ? ' · Stale' : ''}`
              : 'EUR conversion details'}
          </button>
        </Hint>
      </div>
      {query.isError && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 p-4 text-sm"
        >
          <span className="text-destructive">
            {query.error instanceof Error
              ? query.error.message
              : 'Could not load advertising data.'}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void query.refetch()}
          >
            Retry
          </Button>
        </div>
      )}
      {query.isLoading ? (
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {['spend', 'impressions', 'conversations', 'reply'].map((id) => (
            <Skeleton key={id} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : (
        <div
          className="grid grid-cols-2 gap-3 xl:grid-cols-4"
          aria-live="polite"
        >
          <Stat
            label={
              summaryCurrency === 'EUR'
                ? 'Total spent · EUR'
                : `Amount spent · ${summaryCurrency}`
            }
            value={formatMoney(summarySpend, summaryCurrency)}
            note={`${summary?.organizationCount ?? 0} organizations · ${nativeCurrencies.join(' / ') || 'No spend data'}`}
            hint={
              summaryCurrency === 'EUR'
                ? fxHint
                : `Amount spent in this organization’s ad account currency. ${fxHint}`
            }
          />
          <Stat
            label="Impressions"
            value={formatCount(totals.impressions)}
            note={
              cpm === null
                ? 'No delivery data'
                : `${formatMoney(cpm, summaryCurrency)} CPM`
            }
            hint={`Times the filtered ads appeared. CPM means cost per 1,000 impressions, shown in ${summaryCurrency}.`}
          />
          <Stat
            label="Conversations"
            value={formatCount(totals.conversations)}
            note={
              costConversation === null
                ? 'No cost per conversation'
                : `${formatMoney(costConversation, summaryCurrency)} per conversation`
            }
            hint="Conversations created in this period with a stored ad link, including lead-form attribution. Dates use UTC."
          />
          <Stat
            label="Reply %"
            value={replyRate === null ? '—' : `${replyRate.toFixed(1)}%`}
            note={`${formatCount(totals.replied)} conversations with replies`}
            hint="Customer reply rate: conversations with a customer message after the first sent business message, divided by all ad-attributed conversations created in this period."
          />
        </div>
      )}
      {missingRates > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs"
        >
          {missingRates > 0
            ? `${missingRates} ads have spend without an exchange rate. The EUR total is unavailable; native amounts remain visible.`
            : ''}
        </div>
      )}
      {exportError && (
        <p role="alert" className="text-sm text-destructive">
          {exportError}
        </p>
      )}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs
            value={status}
            onValueChange={(value) => {
              setStatus(
                value as NonNullable<GetAdvertisingReportInput['status']>
              );
              setPage(0);
            }}
          >
            <TabsList aria-label="Filter by delivery">
              <TabsTrigger value="all">
                All ads{' '}
                <span className="ml-1 text-xs text-muted-foreground">
                  {query.data?.totalAds ?? 0}
                </span>
              </TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="paused">Paused</TabsTrigger>
              <TabsTrigger value="issues">Issues</TabsTrigger>
            </TabsList>
          </Tabs>
          <DateFilter
            {...dates}
            onChange={(since, until) => {
              setDates({ since, until });
              setPage(0);
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-44 flex-1 md:max-w-xs">
            <Search
              aria-hidden
              className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              aria-label="Search ads, organizations, campaigns, or ad IDs"
              placeholder="Search ads, organizations…"
              maxLength={200}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              className="rounded-full pl-9"
            />
          </div>
          <FilterSelect
            label="Filter by organization"
            value={organization}
            onChange={(value) => {
              setOrganization(value);
              setCampaign('all');
              setPage(0);
            }}
          >
            <SelectItem value="all">All organizations</SelectItem>
            {organizations.map(([id, name]) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </FilterSelect>
          <FilterSelect
            label="Filter by currency"
            value={currency}
            onChange={(value) => {
              setCurrency(value);
              setPage(0);
            }}
          >
            <SelectItem value="all">All currencies</SelectItem>
            {currencies.map((code) => (
              <SelectItem key={code} value={code}>
                {code}
              </SelectItem>
            ))}
          </FilterSelect>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                Filters
                {extraFilters > 0 && (
                  <Badge variant="secondary" className="px-1.5">
                    {extraFilters}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="space-y-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Platform</p>
                <FilterSelect
                  label="Filter by platform"
                  value={platform}
                  onChange={(value) => {
                    setPlatform(value);
                    setPage(0);
                  }}
                >
                  <SelectItem value="all">All platforms</SelectItem>
                  {(query.data?.facets.platforms ?? []).map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </FilterSelect>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Creative</p>
                <FilterSelect
                  label="Filter by format"
                  value={format}
                  onChange={(value) => {
                    setFormat(value);
                    setPage(0);
                  }}
                >
                  <SelectItem value="all">All formats</SelectItem>
                  <SelectItem value="Video">Video</SelectItem>
                  <SelectItem value="Image">Image</SelectItem>
                  <SelectItem value="Unknown">Unknown</SelectItem>
                </FilterSelect>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">
                  Customer replies
                </p>
                <FilterSelect
                  label="Filter by customer replies"
                  value={replies}
                  onChange={(value) => {
                    setReplies(value);
                    setPage(0);
                  }}
                >
                  <SelectItem value="all">All conversations</SelectItem>
                  <SelectItem value="with">Has customer replies</SelectItem>
                  <SelectItem value="without">
                    Conversations, no replies
                  </SelectItem>
                </FilterSelect>
              </div>
            </PopoverContent>
          </Popover>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clear}>
              <X className="size-3.5" />
              Clear
            </Button>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <FilterSelect
              label="Group rows"
              value={preferences.group}
              onChange={(value) => {
                setPreferences({ ...preferences, group: value as GroupBy });
                setPage(0);
              }}
            >
              <SelectItem value="ads">Ads</SelectItem>
              <SelectItem value="organizations">Organizations</SelectItem>
              <SelectItem value="campaigns">Campaigns</SelectItem>
            </FilterSelect>
            <FilterSelect
              label="Metric preset"
              value={preferences.preset}
              onChange={(value) => {
                if (METRIC_PRESETS[value])
                  setPreferences({
                    ...preferences,
                    preset: value,
                    metrics: METRIC_PRESETS[value].metrics,
                  });
              }}
            >
              {Object.entries(METRIC_PRESETS).map(([id, preset]) => (
                <SelectItem key={id} value={id}>
                  {preset.label}
                </SelectItem>
              ))}
              {preferences.preset === 'custom' && (
                <SelectItem value="custom">Custom columns</SelectItem>
              )}
            </FilterSelect>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Choose metric columns"
                >
                  <Columns3 className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="max-h-96 w-64 overflow-y-auto"
              >
                <DropdownMenuLabel>Metric columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {AD_METRICS.map((metric) => (
                  <DropdownMenuCheckboxItem
                    key={metric.id}
                    checked={selectedMetricIds.includes(metric.id)}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(checked) =>
                      setPreferences({
                        ...preferences,
                        preset: 'custom',
                        metrics: checked
                          ? [...selectedMetricIds, metric.id]
                          : selectedMetricIds.filter((id) => id !== metric.id),
                      })
                    }
                  >
                    {metric.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {campaign !== 'all' && (
          <div>
            <Badge variant="secondary" className="gap-1.5">
              Campaign {campaign}
              <button
                type="button"
                aria-label="Clear campaign filter"
                onClick={() => {
                  setCampaign('all');
                  setPage(0);
                }}
              >
                <X className="size-3" />
              </button>
            </Badge>
          </div>
        )}
      </div>
      <div>
        {query.isLoading ? (
          <div className="space-y-3 py-3">
            {['one', 'two', 'three', 'four'].map((id) => (
              <Skeleton key={id} className="h-14 w-full" />
            ))}
          </div>
        ) : mobile ? (
          <div className="divide-y overflow-hidden rounded-xl border">
            {pageRows.map((row) => (
              <button
                type="button"
                key={row.id}
                className="block w-full space-y-2.5 p-4 text-left hover:bg-muted/40"
                onClick={() => chooseRow(row)}
                data-testid="ad-performance-row"
                data-ad-id={row.id}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{row.name}</p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {row.organization} · {row.currency ?? 'Unknown currency'}
                    </p>
                  </div>
                  <p className="shrink-0 text-sm font-semibold tabular-nums">
                    {formatMoney(row.spend, row.currency)}
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {PRIORITY_METRICS.map((id) => {
                    const definition = AD_METRICS.find(
                      (item) => item.id === id
                    );
                    const metric = definition
                      ? describeMetric(definition, row.conversionKind)
                      : undefined;
                    return metric ? (
                      <div key={id}>
                        <p
                          className="text-xs text-muted-foreground"
                          title={`${metric.name}. ${metric.description}`}
                        >
                          {metric.label}
                        </p>
                        <p className="mt-0.5 text-sm tabular-nums">
                          {formatMetric(
                            metric,
                            metric.value(row),
                            row.currency
                          )}
                        </p>
                      </div>
                    ) : null;
                  })}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <Table className="text-xs" aria-label="Advertising performance">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead
                  className="sticky left-0 z-10 w-72 min-w-72 max-w-72 border-r bg-background pl-0 pr-3 text-xs font-normal text-muted-foreground"
                  aria-sort={
                    sort.id === 'name'
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-1"
                    onClick={() => changeSort('name')}
                  >
                    {preferences.group === 'ads'
                      ? 'Ad'
                      : preferences.group === 'organizations'
                        ? 'Organization'
                        : 'Campaign'}
                    {sortIcon('name')}
                  </button>
                </TableHead>
                {preferences.group !== 'organizations' && (
                  <TableHead className="min-w-36 text-xs font-normal text-muted-foreground">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1"
                      onClick={() => changeSort('organization')}
                    >
                      Organization{sortIcon('organization')}
                    </button>
                  </TableHead>
                )}
                <TableHead className="text-xs font-normal text-muted-foreground">
                  {preferences.group === 'ads' ? 'Delivery' : 'Ads'}
                </TableHead>
                {metrics.map((metric) => (
                  <TableHead
                    key={metric.id}
                    className="text-right text-xs font-normal text-muted-foreground"
                    aria-sort={
                      sort.id === metric.id
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <Hint
                      key={metric.name}
                      label={`${metric.name}. ${metric.description} Source: ${metric.source}.${metric.format === 'money' ? ' Shown in account currency; sorted using its EUR value.' : ''}`}
                    >
                      <button
                        type="button"
                        className="inline-flex items-center justify-end gap-1 py-2 hover:text-foreground"
                        onClick={() => changeSort(metric.id)}
                      >
                        <span className="underline decoration-dotted decoration-muted-foreground/50 underline-offset-4">
                          {metric.label}
                        </span>
                        {sortIcon(metric.id)}
                      </button>
                    </Hint>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((row) => (
                <TableRow
                  key={row.id}
                  className="group cursor-pointer focus-within:bg-muted/50"
                  onClick={() => chooseRow(row)}
                  data-testid="ad-performance-row"
                  data-ad-id={row.id}
                >
                  <TableCell className="sticky left-0 z-10 w-72 min-w-72 max-w-72 border-r bg-background py-3.5 pl-0 pr-3 group-hover:bg-muted group-focus-within:bg-muted">
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-9 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
                        {row.grouped ? (
                          <Building2 aria-hidden className="size-4" />
                        ) : row.format === 'Video' ? (
                          <Video aria-hidden className="size-4" />
                        ) : (
                          <Image aria-hidden className="size-4" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <Hint label={row.name}>
                          <button
                            type="button"
                            className="block w-full truncate rounded-sm text-left font-medium hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
                            aria-haspopup={row.grouped ? undefined : 'dialog'}
                          >
                            {row.name}
                          </button>
                        </Hint>
                        <Hint label={row.campaign}>
                          <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                            {row.campaign}
                          </span>
                        </Hint>
                      </div>
                    </div>
                  </TableCell>
                  {preferences.group !== 'organizations' && (
                    <TableCell className="py-3.5">
                      <p className="max-w-40 truncate" title={row.organization}>
                        {row.organization}
                      </p>
                      <Hint label={currencyName(row.currency)}>
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">
                          {row.platform} · {row.currency ?? 'Unknown currency'}
                        </span>
                      </Hint>
                    </TableCell>
                  )}
                  <TableCell className="py-3.5">
                    {row.grouped ? (
                      <span>
                        {row.adCount}{' '}
                        <span className="text-muted-foreground">
                          · {row.activeAds} active · {row.currency ?? '—'}
                        </span>
                      </span>
                    ) : (
                      <DeliveryBadge status={row.status} />
                    )}
                  </TableCell>
                  {metrics.map((metric) => (
                    <TableCell
                      key={metric.id}
                      className={cn(
                        'py-3.5 text-right tabular-nums',
                        [
                          'costConversion',
                          'cpl',
                          'cpc',
                          'spend',
                          'conversations',
                          'replyRate',
                        ].includes(metric.id) && 'font-medium'
                      )}
                    >
                      {metric.id === 'costConversion' ? (
                        <Hint
                          label={`${describeMetric(metric, row.conversionKind).name}. ${describeMetric(metric, row.conversionKind).description}`}
                        >
                          <span className="inline-flex flex-col items-end gap-0.5">
                            {formatMetric(
                              metric,
                              metric.value(row),
                              row.currency
                            )}
                            <span className="text-[10px] font-normal text-muted-foreground">
                              {describeMetric(metric, row.conversionKind).label}
                            </span>
                          </span>
                        </Hint>
                      ) : metric.id === 'replyRate' ? (
                        <span className="inline-flex items-center justify-end gap-2">
                          <Progress
                            aria-label={`${row.name}: customer reply rate`}
                            className="hidden h-1 w-8 xl:block"
                            value={metric.value(row) ?? 0}
                          />
                          {formatMetric(
                            metric,
                            metric.value(row),
                            row.currency
                          )}
                        </span>
                      ) : (
                        formatMetric(metric, metric.value(row), row.currency)
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!query.isLoading && !query.isError && pageRows.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <Search className="mb-1 size-6 text-muted-foreground" />
            <p className="text-sm font-medium">No matching ads</p>
            <p className="text-xs text-muted-foreground">
              Try All ads, change the dates, or clear your filters.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                clear();
                setStatus('all');
              }}
            >
              Show all ads
            </Button>
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-4 text-xs text-muted-foreground">
          <span aria-live="polite">
            {totalRows
              ? `${currentPage * Number(pageSize) + 1}–${Math.min((currentPage + 1) * Number(pageSize), totalRows)} of ${totalRows} ${preferences.group}`
              : `0 ${preferences.group}`}{' '}
            · {summary?.adCount ?? 0} ads
            {noData > 0 && ` · ${noData} without delivery data in this period`}
          </span>
          <div className="flex items-center gap-2">
            <span>Rows</span>
            <FilterSelect
              label="Rows per page"
              value={pageSize}
              onChange={(value) => {
                setPageSize(value);
                setPage(0);
              }}
            >
              {['10', '25', '50', '100'].map((size) => (
                <SelectItem key={size} value={size}>
                  {size}
                </SelectItem>
              ))}
            </FilterSelect>
            <span className="mx-1">
              Page {currentPage + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Previous page"
              disabled={query.isFetching || currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Next page"
              disabled={query.isFetching || currentPage + 1 >= totalPages}
              onClick={() => setPage(currentPage + 1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span>
          Meta: delivery & performance. Borradh: conversations & replies.
        </span>
        <Hint label="Daily unique reach cannot be added across days or ads. Select one day and one ad to see reach and frequency.">
          <button
            type="button"
            className="cursor-help underline decoration-dotted underline-offset-4"
          >
            — means unavailable · Native row currencies · EUR overview
          </button>
        </Hint>
      </div>
      <AdvertisingDetail ad={selectedAd} onClose={() => setSelectedAd(null)} />
    </div>
  );
}
